import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiRequest, getApiUrl, haySesionActiva } from '../config/api';
import SuccessToast from './SuccessToast';
import { extractTokens, isNumberedTemplate, namedExamples, replaceToken, validateVarNames } from '../utils/templateVars';
import { leerCuerpo, motivoDelError } from '../utils/errores';
import CampanasTab from './campanas/CampanasTab';

/**
 * 📋 Panel de gestión de plantillas de WhatsApp.
 *
 * Permite listar, crear y eliminar plantillas de mensaje de un bot contra la
 * Graph API de Meta (a través del backend):
 *   - GET    /whatsapp/templates?botId=            → listado en vivo desde Meta
 *   - POST   /whatsapp/templates                   → crear
 *   - DELETE /whatsapp/templates?botId=&name=      → eliminar
 *   - POST   /whatsapp/templates/media (multipart) → subir media de cabecera
 *
 * Incluye vista previa en vivo estilo burbuja de WhatsApp.
 */

const LANGUAGES = [
  { code: 'es', label: 'Español' },
  { code: 'es_ES', label: 'Español (España)' },
  { code: 'es_MX', label: 'Español (México)' },
  { code: 'es_AR', label: 'Español (Argentina)' },
  { code: 'en', label: 'Inglés' },
  { code: 'en_US', label: 'Inglés (EE. UU.)' },
  { code: 'pt_BR', label: 'Portugués (Brasil)' },
  { code: 'pt_PT', label: 'Portugués (Portugal)' },
  { code: 'fr', label: 'Francés' },
  { code: 'de', label: 'Alemán' },
  { code: 'it', label: 'Italiano' },
];

const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing', desc: 'Promociones, ofertas, novedades' },
  { value: 'UTILITY', label: 'Utilidad', desc: 'Confirmaciones, actualizaciones de pedidos' },
  { value: 'AUTHENTICATION', label: 'Autenticación', desc: 'Códigos de verificación (OTP)' },
];

const HEADER_TYPES = [
  { value: 'NONE', label: 'Sin cabecera' },
  { value: 'TEXT', label: 'Texto' },
  { value: 'IMAGE', label: 'Imagen' },
  { value: 'VIDEO', label: 'Vídeo' },
  { value: 'DOCUMENT', label: 'Documento' },
];

const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Respuesta rápida' },
  { value: 'URL', label: 'Enlace (URL)' },
  { value: 'PHONE_NUMBER', label: 'Llamar (teléfono)' },
];

const emptyForm = () => ({
  name: '',
  language: 'es',
  category: 'MARKETING',
  headerType: 'NONE',
  headerText: '',
  headerTextExample: '',
  headerMediaHandle: '',
  headerMediaFileName: '',
  bodyText: '',
  bodyExamples: {},
  footerText: '',
  buttons: [],
});

// Estados en los que Meta permite editar una plantilla.
const EDITABLE_STATUSES = ['APPROVED', 'REJECTED', 'PAUSED'];
const isEditable = (status) => EDITABLE_STATUSES.includes((status || '').toUpperCase());

// Genera una Idempotency-Key para que reintentos de red no dupliquen la
// operación en el backend (crear/editar). Fallback para navegadores sin
// crypto.randomUUID.
const newIdemKey = () => {
  try {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch (_) { /* noop */ }
  return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

// Traduce una respuesta de error del backend a un mensaje legible: ver utils/errores.
const describeError = (res, data, fallback) => motivoDelError(res && res.status, data, fallback);

// Convierte una plantilla (componentes de Meta) al estado del formulario para editarla.
const formFromTemplate = (t) => {
  const f = emptyForm();
  f.name = t.name || '';
  f.language = t.language || 'es';
  f.category = (t.category || 'MARKETING').toUpperCase();

  const comps = Array.isArray(t.components) ? t.components : [];
  for (const c of comps) {
    const type = (c.type || '').toUpperCase();
    if (type === 'HEADER') {
      const fmt = (c.format || 'TEXT').toUpperCase();
      if (fmt === 'TEXT') {
        f.headerType = 'TEXT';
        f.headerText = c.text || '';
        f.headerTextExample = namedExamples(c, 'header_text_named_params')[extractTokens(c.text)[0]] || '';
      } else {
        // IMAGE / VIDEO / DOCUMENT: Meta no devuelve el handle original, hay que
        // volver a subir el archivo al editar (headerMediaHandle queda vacío).
        f.headerType = fmt;
      }
    } else if (type === 'BODY') {
      f.bodyText = c.text || '';
      f.bodyExamples = namedExamples(c, 'body_text_named_params');
    } else if (type === 'FOOTER') {
      f.footerText = c.text || '';
    } else if (type === 'BUTTONS') {
      f.buttons = (c.buttons || []).map((b) => ({
        type: (b.type || 'QUICK_REPLY').toUpperCase(),
        text: b.text || '',
        url: b.url || '',
        phone_number: b.phone_number || '',
      }));
    }
  }
  return f;
};

const statusColor = (status) => {
  const s = (status || '').toUpperCase();
  if (s === 'APPROVED') return { bg: '#dcfce7', fg: '#166534' };
  if (s === 'PENDING' || s === 'IN_APPEAL' || s === 'PENDING_DELETION') return { bg: '#fef9c3', fg: '#854d0e' };
  if (s === 'REJECTED' || s === 'DISABLED' || s === 'PAUSED') return { bg: '#fee2e2', fg: '#991b1b' };
  return { bg: '#e5e7eb', fg: '#374151' };
};

// Devuelve los componentes relevantes (header/body/buttons) de una plantilla.
const splitComponents = (t) => {
  const comps = Array.isArray(t?.components) ? t.components : [];
  const header = comps.find((c) => (c.type || '').toUpperCase() === 'HEADER') || null;
  const body = comps.find((c) => (c.type || '').toUpperCase() === 'BODY') || null;
  const buttons = comps.find((c) => (c.type || '').toUpperCase() === 'BUTTONS') || null;
  return { header, body, buttons };
};

const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

// Extrae los tokens de variables {{1}} o {{nombre}} de un texto, en orden y sin
// duplicados. Si todos son numéricos (formato POSITIONAL) se ordenan numéricamente;
// si hay nombres (formato NAMED) se conserva el orden de aparición.
const extractVarTokens = (text) => {
  const seen = new Set();
  const out = [];
  const re = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
  let m;
  while ((m = re.exec(text || '')) !== null) {
    const tok = m[1];
    if (!seen.has(tok)) { seen.add(tok); out.push(tok); }
  }
  if (out.length > 0 && out.every((t) => /^\d+$/.test(t))) {
    out.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  }
  return out;
};

// Un token es "con nombre" (NAMED) si no es puramente numérico.
const isNamedToken = (tok) => !/^\d+$/.test(tok);

// Construye un parámetro de texto Graph API; añade parameter_name si es NAMED.
const buildTextParam = (tok, value) => (
  isNamedToken(tok)
    ? { type: 'text', text: value, parameter_name: tok }
    : { type: 'text', text: value }
);

// Deriva un nombre de archivo de una URL (para cabecera de documento).
const fileNameFromUrl = (url) => {
  const clean = (url || '').split('?')[0].split('#')[0];
  const last = clean.substring(clean.lastIndexOf('/') + 1);
  return last || '';
};

/**
 * 📨 Vista de envío de plantillas.
 *
 * Permite elegir una plantilla APROBADA, rellenar sus variables ({{1}}, {{2}}…)
 * del cuerpo y de la cabecera de texto, aportar la media de cabecera (subiéndola
 * para obtener un media_id, o pegando una URL pública) e indicar el teléfono
 * destino, y enviarla vía POST /whatsapp/send-template.
 */
function SendTemplateView({ botId, templates, loading, reload, onSuccess, onError }) {
  const [selectedKey, setSelectedKey] = useState('');
  const [phone, setPhone] = useState('');
  const [bodyValues, setBodyValues] = useState({});
  const [headerTextValues, setHeaderTextValues] = useState({});
  const [buttonValues, setButtonValues] = useState({}); // { [buttonIndex]: valor }
  const [mediaMode, setMediaMode] = useState('upload'); // 'upload' | 'url'
  const [mediaId, setMediaId] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaFileName, setMediaFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const fileRef = useRef(null);

  const failSend = (msg) => {
    setSendError(msg);
    if (onError) onError(msg);
  };

  // Solo se pueden enviar plantillas aprobadas por Meta.
  const approved = useMemo(
    () => (Array.isArray(templates) ? templates.filter((t) => (t.status || '').toUpperCase() === 'APPROVED') : []),
    [templates]
  );

  const keyOf = (t) => `${t.name}||${t.language}`;
  const selected = useMemo(() => approved.find((t) => keyOf(t) === selectedKey) || null, [approved, selectedKey]);

  const { header, body, buttons } = useMemo(() => (selected ? splitComponents(selected) : { header: null, body: null, buttons: null }), [selected]);

  const headerFormat = (header?.format || '').toUpperCase();
  const isMediaHeader = MEDIA_FORMATS.includes(headerFormat);
  const headerTextVars = useMemo(() => (headerFormat === 'TEXT' ? extractVarTokens(header?.text) : []), [headerFormat, header]);
  const bodyVars = useMemo(() => extractVarTokens(body?.text), [body]);

  // Botones de URL con variable dinámica: {{1}} o {{nombre}} en su url.
  // Meta identifica cada botón por su índice (0-based) dentro de TODOS los botones.
  const urlButtons = useMemo(() => {
    const arr = Array.isArray(buttons?.buttons) ? buttons.buttons : [];
    return arr
      .map((b, idx) => ({ b, idx }))
      .filter(({ b }) => (b.type || '').toUpperCase() === 'URL' && extractVarTokens(b.url).length > 0)
      .map(({ b, idx }) => ({ idx, url: b.url || '', text: b.text || '', token: extractVarTokens(b.url)[0] }));
  }, [buttons]);

  // Al cambiar de plantilla, limpiar los valores introducidos.
  const onSelect = (key) => {
    setSelectedKey(key);
    setBodyValues({});
    setHeaderTextValues({});
    setButtonValues({});
    setMediaId('');
    setMediaUrl('');
    setMediaFileName('');
    setMediaMode('upload');
    setSendError(null);
  };

  const acceptForFormat = headerFormat === 'IMAGE'
    ? 'image/*'
    : headerFormat === 'VIDEO'
      ? 'video/*'
      : headerFormat === 'DOCUMENT'
        ? '.pdf'
        : '';

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setSendError(null);
    try {
      if (!haySesionActiva()) return;
      const fd = new FormData();
      fd.append('botId', String(botId));
      fd.append('file', file);
      const res = await fetch(getApiUrl('/whatsapp/templates/send-media'), {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.success && data.mediaId) {
        setMediaId(data.mediaId);
        setMediaFileName(file.name);
      } else {
        failSend(describeError(res, data, 'No se pudo subir el archivo.'));
      }
    } catch (e) {
      failSend('Error subiendo el archivo.');
    } finally {
      setUploading(false);
    }
  };

  // Construye los componentes en formato Graph API para el envío.
  const buildSendComponents = () => {
    const components = [];

    if (headerFormat === 'TEXT' && headerTextVars.length > 0) {
      const params = headerTextVars.map((tok) => buildTextParam(tok, (headerTextValues[tok] || '').trim()));
      components.push({ type: 'header', parameters: params });
    } else if (isMediaHeader) {
      const media = mediaMode === 'upload'
        ? { id: mediaId }
        : { link: mediaUrl.trim() };
      const key = headerFormat.toLowerCase(); // image | video | document
      // Meta permite (y conviene) un filename en cabeceras de documento.
      if (headerFormat === 'DOCUMENT') {
        const fname = mediaMode === 'upload' ? mediaFileName : fileNameFromUrl(mediaUrl);
        if (fname) media.filename = fname;
      }
      const param = { type: key, [key]: media };
      components.push({ type: 'header', parameters: [param] });
    }

    if (bodyVars.length > 0) {
      const params = bodyVars.map((tok) => buildTextParam(tok, (bodyValues[tok] || '').trim()));
      components.push({ type: 'body', parameters: params });
    }

    // Botones de URL dinámicos: un componente por botón, identificado por su índice.
    // El parámetro es siempre posicional (sustituye el sufijo de la URL), sin nombre.
    urlButtons.forEach(({ idx }) => {
      components.push({
        type: 'button',
        sub_type: 'url',
        index: String(idx),
        parameters: [{ type: 'text', text: (buttonValues[idx] || '').trim() }],
      });
    });

    return components;
  };

  const validateSend = () => {
    if (!selected) return 'Selecciona una plantilla.';
    const cleanedPhone = phone.replace(/[^\d]/g, '');
    if (cleanedPhone.length < 8) return 'Indica un número de teléfono válido con prefijo de país.';
    for (const tok of headerTextVars) {
      if (!(headerTextValues[tok] || '').trim()) return `Rellena el valor de la variable {{${tok}}} de la cabecera.`;
    }
    for (const tok of bodyVars) {
      if (!(bodyValues[tok] || '').trim()) return `Rellena el valor de la variable {{${tok}}} del cuerpo.`;
    }
    if (isMediaHeader) {
      if (mediaMode === 'upload' && !mediaId) return 'Sube el archivo de la cabecera antes de enviar.';
      if (mediaMode === 'url') {
        const u = mediaUrl.trim();
        if (!u) return 'Indica la URL pública de la media de la cabecera.';
        if (!/^https:\/\//i.test(u)) return 'La URL de la media debe empezar por https://';
      }
    }
    for (const { idx, text } of urlButtons) {
      const v = (buttonValues[idx] || '').trim();
      if (!v) return `Rellena el valor de la variable del botón «${text || 'URL'}».`;
      if (/\s/.test(v)) return `El valor del botón «${text || 'URL'}» no puede contener espacios.`;
    }
    return null;
  };

  const resetSendForm = () => {
    setSelectedKey('');
    setPhone('');
    setBodyValues({});
    setHeaderTextValues({});
    setButtonValues({});
    setMediaId('');
    setMediaUrl('');
    setMediaFileName('');
    setMediaMode('upload');
    setSendError(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSend = async () => {
    setSendError(null);
    const err = validateSend();
    if (err) { failSend(err); return; }
    setSending(true);
    try {
      const payload = {
        botId,
        recipientPhoneNumber: phone.replace(/[^\d]/g, ''),
        templateName: selected.name,
        languageCode: selected.language,
        components: buildSendComponents(),
      };
      const res = await apiRequest(getApiUrl('/whatsapp/send-template'), {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        resetSendForm();
        if (onSuccess) onSuccess(`Plantilla enviada a ${payload.recipientPhoneNumber}.`);
      } else {
        failSend(describeError(res, data, 'No se pudo enviar la plantilla.'));
      }
    } catch (e) {
      failSend('Error de conexión al enviar la plantilla.');
    } finally {
      setSending(false);
    }
  };

  // Vista previa con variables sustituidas.
  const previewHeaderText = useMemo(() => {
    if (headerFormat !== 'TEXT') return '';
    let text = header?.text || '';
    headerTextVars.forEach((n) => {
      text = text.replace(new RegExp(`\\{\\{${n}\\}\\}`, 'g'), (headerTextValues[n] || '').trim() || `{{${n}}}`);
    });
    return text;
  }, [headerFormat, header, headerTextVars, headerTextValues]);

  const previewBody = useMemo(() => {
    let text = body?.text || '';
    bodyVars.forEach((n) => {
      text = text.replace(new RegExp(`\\{\\{${n}\\}\\}`, 'g'), (bodyValues[n] || '').trim() || `{{${n}}}`);
    });
    return text;
  }, [body, bodyVars, bodyValues]);

  const footerComp = useMemo(() => {
    const comps = Array.isArray(selected?.components) ? selected.components : [];
    return comps.find((c) => (c.type || '').toUpperCase() === 'FOOTER') || null;
  }, [selected]);

  return (
    <div style={S.formLayout}>
      <div style={S.formCol}>
        <h3 style={S.formTitle}>Enviar plantilla</h3>
        <p style={{ ...S.hint, marginTop: 4, marginBottom: 14 }}>
          Solo se muestran las plantillas aprobadas por Meta.
        </p>

        {sendError ? <div style={S.banner('error')}>{sendError}</div> : null}

        <div style={S.field}>
          <label style={S.label}>Plantilla *</label>
          <select style={S.input} value={selectedKey} onChange={(e) => onSelect(e.target.value)}>
            <option value="">{loading ? 'Cargando…' : 'Selecciona una plantilla…'}</option>
            {approved.map((t) => (
              <option key={keyOf(t)} value={keyOf(t)}>{t.name} ({t.language}) · {t.category}</option>
            ))}
          </select>
          {!loading && approved.length === 0 && (
            <span style={S.hint}>No hay plantillas aprobadas para este bot.</span>
          )}
        </div>

        <div style={S.field}>
          <label style={S.label}>Teléfono del destinatario *</label>
          <input
            style={S.input}
            value={phone}
            placeholder="p.ej. 34600000000 (con prefijo de país)"
            onChange={(e) => setPhone(e.target.value)}
          />
          <span style={S.hint}>Formato internacional, sin «+» ni espacios (se limpian automáticamente).</span>
        </div>

        {selected && isMediaHeader && (
          <div style={S.field}>
            <label style={S.label}>Cabecera ({headerFormat.toLowerCase()})</label>
            <div style={S.row}>
              <button
                style={{ ...S.btnSmall, ...(mediaMode === 'upload' ? {} : S.btnSmallInactive) }}
                onClick={() => setMediaMode('upload')}
              >Subir archivo</button>
              <button
                style={{ ...S.btnSmall, ...(mediaMode === 'url' ? {} : S.btnSmallInactive) }}
                onClick={() => setMediaMode('url')}
              >Usar URL</button>
            </div>
            {mediaMode === 'upload' ? (
              <div style={{ marginTop: 8 }}>
                <input
                  ref={fileRef}
                  type="file"
                  accept={acceptForFormat}
                  style={{ display: 'none' }}
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
                <button style={S.btnUpload} onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? 'Subiendo…' : mediaId ? '✓ Archivo cargado — cambiar' : `Subir ${headerFormat.toLowerCase()}`}
                </button>
                {mediaFileName && <span style={S.hint}>{mediaFileName}</span>}
              </div>
            ) : (
              <input
                style={{ ...S.input, marginTop: 8 }}
                value={mediaUrl}
                placeholder="https://… (URL pública del archivo)"
                onChange={(e) => setMediaUrl(e.target.value)}
              />
            )}
          </div>
        )}

        {selected && headerTextVars.length > 0 && (
          <div style={S.field}>
            <label style={S.label}>Variables de la cabecera</label>
            {headerTextVars.map((n) => (
              <div key={n} style={S.varRow}>
                <span style={S.varTag}>{`{{${n}}}`}</span>
                <input
                  style={{ ...S.input, flex: 1 }}
                  value={headerTextValues[n] || ''}
                  placeholder={`Valor para {{${n}}}`}
                  onChange={(e) => setHeaderTextValues({ ...headerTextValues, [n]: e.target.value })}
                />
              </div>
            ))}
          </div>
        )}

        {selected && bodyVars.length > 0 && (
          <div style={S.field}>
            <label style={S.label}>Variables del cuerpo</label>
            {bodyVars.map((n) => (
              <div key={n} style={S.varRow}>
                <span style={S.varTag}>{`{{${n}}}`}</span>
                <input
                  style={{ ...S.input, flex: 1 }}
                  value={bodyValues[n] || ''}
                  placeholder={`Valor para {{${n}}}`}
                  onChange={(e) => setBodyValues({ ...bodyValues, [n]: e.target.value })}
                />
              </div>
            ))}
          </div>
        )}

        {selected && urlButtons.length > 0 && (
          <div style={S.field}>
            <label style={S.label}>Variables de botones (URL)</label>
            {urlButtons.map(({ idx, text, url, token }) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <div style={S.varRow}>
                  <span style={S.varTag}>{`{{${token}}}`}</span>
                  <input
                    style={{ ...S.input, flex: 1 }}
                    value={buttonValues[idx] || ''}
                    placeholder={`Valor para el botón «${text || 'URL'}»`}
                    onChange={(e) => setButtonValues({ ...buttonValues, [idx]: e.target.value })}
                  />
                </div>
                <span style={S.hint}>{url.replace(/\{\{\s*[A-Za-z0-9_]+\s*\}\}/, (buttonValues[idx] || '').trim() || `{{${token}}}`)}</span>
              </div>
            ))}
          </div>
        )}

        {selected && headerTextVars.length === 0 && bodyVars.length === 0 && urlButtons.length === 0 && !isMediaHeader && (
          <div style={S.banner('info')}>Esta plantilla no tiene variables. Solo indica el teléfono y envía.</div>
        )}

        <div style={S.formActions}>
          <button style={S.btnGhost} onClick={reload} disabled={loading}>{loading ? 'Cargando…' : 'Actualizar lista'}</button>
          <button style={S.btnPrimary} onClick={handleSend} disabled={sending || uploading || !selected}>
            {sending ? 'Enviando…' : 'Enviar plantilla'}
          </button>
        </div>
      </div>

      {/* Vista previa */}
      <div style={S.previewCol}>
        <span style={S.previewTitle}>Vista previa</span>
        <div style={S.phone}>
          {selected ? (
            <>
              <div style={S.bubble}>
                {headerFormat === 'TEXT' && previewHeaderText && <div style={S.bubbleHeader}>{previewHeaderText}</div>}
                {isMediaHeader && (
                  <div style={S.bubbleMedia}>
                    {headerFormat === 'IMAGE' ? '🖼️ Imagen' : headerFormat === 'VIDEO' ? '🎬 Vídeo' : '📄 Documento'}
                  </div>
                )}
                <div style={S.bubbleBody}>{previewBody || 'El cuerpo del mensaje aparecerá aquí…'}</div>
                {footerComp?.text && <div style={S.bubbleFooter}>{footerComp.text}</div>}
                <div style={S.bubbleTime}>12:00</div>
              </div>
              {buttons?.buttons?.length > 0 && (
                <div style={S.bubbleButtons}>
                  {buttons.buttons.map((b, i) => (
                    <div key={i} style={S.previewButton}>
                      {(b.type || '').toUpperCase() === 'URL' ? '🔗 ' : (b.type || '').toUpperCase() === 'PHONE_NUMBER' ? '📞 ' : '↩️ '}{b.text}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ ...S.bubble, color: '#8696a0' }}>Selecciona una plantilla para ver la vista previa.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function WhatsAppTemplatesPanel({ botId, zonaHoraria }) {
  const [tab, setTab] = useState('manage'); // 'manage' | 'send' | 'campaigns'
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null); // { id, status } cuando se edita
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [errorToast, setErrorToast] = useState(null);
  const [cooldownPopup, setCooldownPopup] = useState(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [newVarName, setNewVarName] = useState('');

  const failSubmit = (msg) => {
    setSubmitError(msg);
    setSubmitSuccess(null);
    setErrorToast(msg);
  };
  const [deletingName, setDeletingName] = useState(null);
  const [plantillaAEliminar, setPlantillaAEliminar] = useState(null);
  const fileInputRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest(getApiUrl(`/whatsapp/templates?botId=${botId}`));
      const data = await res.json();
      if (res.ok && data.success) {
        setTemplates(Array.isArray(data.data) ? data.data : []);
      } else {
        setError(data.message || 'No se pudieron cargar las plantillas.');
        setTemplates([]);
      }
    } catch (e) {
      setError('Error de conexión al cargar las plantillas.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [botId]);

  // Reconciliación best-effort Meta↔BD. Se usa cuando una operación devolvió
  // persistedLocally=false (Meta OK pero la copia local no se guardó).
  const reconcile = useCallback(async () => {
    if (!botId) return;
    try {
      await apiRequest(getApiUrl(`/whatsapp/templates/reconcile?botId=${botId}`), { method: 'POST' });
    } catch (_) {
      // Silencioso: es una reparación oportunista; el listado se recarga igual.
    }
  }, [botId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const bodyVars = useMemo(() => extractTokens(form.bodyText), [form.bodyText]);
  const headerVars = useMemo(() => extractTokens(form.headerText), [form.headerText]);
  const headerHasVar = headerVars.length > 0;
  const hasVars = bodyVars.length > 0 || (form.headerType === 'TEXT' && headerHasVar);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const isMediaHeader = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerType);

  const acceptForHeader = form.headerType === 'IMAGE'
    ? 'image/*'
    : form.headerType === 'VIDEO'
      ? 'video/*'
      : form.headerType === 'DOCUMENT'
        ? '.pdf'
        : '';

  const handleMediaUpload = async (file) => {
    if (!file) return;
    setMediaUploading(true);
    setSubmitError(null);
    try {
      if (!haySesionActiva()) return;
      const fd = new FormData();
      fd.append('botId', String(botId));
      fd.append('file', file);
      const res = await fetch(getApiUrl('/whatsapp/templates/media'), {
        method: 'POST',
        // Nota: NO fijamos Content-Type para que el navegador ponga el
        // boundary del multipart. Sí incluimos X-Requested-With porque el
        // filtro CSRF del backend lo exige (lo añade apiRequest por defecto,
        // pero aquí usamos fetch directo). La auth va por la cookie httpOnly.
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: fd,
      });
      const data = await leerCuerpo(res);
      if (res.ok && data.success && data.handle) {
        update({ headerMediaHandle: data.handle, headerMediaFileName: file.name });
      } else {
        failSubmit(describeError(res, data, 'No se pudo subir el archivo.'));
      }
    } catch (e) {
      failSubmit('Error subiendo el archivo.');
    } finally {
      setMediaUploading(false);
    }
  };

  const addButton = () => {
    if (form.buttons.length >= 10) return;
    update({ buttons: [...form.buttons, { type: 'QUICK_REPLY', text: '', url: '', phone_number: '' }] });
  };
  const updateButton = (i, patch) => {
    const next = form.buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
    update({ buttons: next });
  };
  const removeButton = (i) => update({ buttons: form.buttons.filter((_, idx) => idx !== i) });

  const insertVariable = () => {
    const name = newVarName.trim();
    const err = validateVarNames([name]);
    if (!name || err) {
      failSubmit(err || 'Escribe el nombre de la variable.');
      return;
    }
    update({ bodyText: `${form.bodyText}{{${name}}}` });
    setNewVarName('');
  };

  // Construye el array de componentes en formato Graph API.
  const buildComponents = () => {
    const components = [];

    if (form.headerType === 'TEXT' && form.headerText.trim()) {
      const header = { type: 'HEADER', format: 'TEXT', text: form.headerText.trim() };
      if (headerHasVar && form.headerTextExample.trim()) {
        header.example = { header_text_named_params: [{ param_name: headerVars[0], example: form.headerTextExample.trim() }] };
      }
      components.push(header);
    } else if (isMediaHeader && form.headerMediaHandle) {
      components.push({
        type: 'HEADER',
        format: form.headerType,
        example: { header_handle: [form.headerMediaHandle] },
      });
    }

    const body = { type: 'BODY', text: form.bodyText.trim() };
    if (bodyVars.length > 0) {
      body.example = { body_text_named_params: bodyVars.map((n) => ({
        param_name: n,
        example: (form.bodyExamples[n] || '').trim() || `ejemplo_${n}`,
      })) };
    }
    components.push(body);

    if (form.footerText.trim()) {
      components.push({ type: 'FOOTER', text: form.footerText.trim() });
    }

    const validButtons = form.buttons
      .filter((b) => b.text.trim())
      .map((b) => {
        if (b.type === 'URL') return { type: 'URL', text: b.text.trim(), url: b.url.trim() };
        if (b.type === 'PHONE_NUMBER') return { type: 'PHONE_NUMBER', text: b.text.trim(), phone_number: b.phone_number.trim() };
        return { type: 'QUICK_REPLY', text: b.text.trim() };
      });
    if (validButtons.length > 0) {
      components.push({ type: 'BUTTONS', buttons: validButtons });
    }

    return components;
  };

  const validate = () => {
    if (!/^[a-z0-9_]{1,512}$/.test(form.name.trim())) {
      return 'El nombre solo admite minúsculas, números y guiones bajos.';
    }
    if (!form.bodyText.trim()) return 'El cuerpo del mensaje es obligatorio.';
    // Meta fija parameter_format al crear: una plantilla sin variables no puede ganarlas editándola.
    if (editing?.sinVariables && hasVars) return 'Esta plantilla se creó sin variables; para añadirlas, crea una plantilla nueva.';
    const headerParaValidar = form.headerType === 'TEXT' ? headerVars : [];
    const varError = validateVarNames([...headerParaValidar, ...bodyVars]);
    if (varError) return varError;
    if (headerParaValidar.length > 1) return 'La cabecera admite una sola variable.';
    if (isMediaHeader && !form.headerMediaHandle) return 'Sube el archivo de la cabecera antes de continuar.';
    if (headerParaValidar.length > 0 && !form.headerTextExample.trim()) return 'Indica un ejemplo para la variable de la cabecera.';
    for (const b of form.buttons) {
      if (b.text.trim() && b.type === 'URL' && !b.url.trim()) return 'Los botones de tipo URL necesitan una dirección.';
      if (b.text.trim() && b.type === 'PHONE_NUMBER' && !b.phone_number.trim()) return 'Los botones de teléfono necesitan un número.';
    }
    return null;
  };

  const startEdit = (t) => {
    const inicial = formFromTemplate(t);
    const variablesOriginales = [
      ...extractTokens(inicial.bodyText),
      ...(inicial.headerType === 'TEXT' ? extractTokens(inicial.headerText) : []),
    ];
    setEditing({ id: t.id, status: t.status, sinVariables: variablesOriginales.length === 0 });
    setForm(inicial);
    setShowForm(true);
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm());
    setSubmitError(null);
  };

  // ¿Se puede editar la categoría? Meta no permite cambiarla en plantillas APPROVED.
  const categoryLocked = editing && (editing.status || '').toUpperCase() === 'APPROVED';

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitSuccess(null);
    setErrorToast(null);
    const validationError = validate();
    if (validationError) {
      failSubmit(validationError);
      return;
    }
    setSubmitting(true);
    // Misma key para toda la operación: si el reintento interno por 401 (o la
    // red) reenvía la petición, el backend no la duplica.
    const idemKey = newIdemKey();
    try {
      const components = buildComponents();
      if (editing) {
        // Edición: nombre e idioma no cambian; categoría solo si no está bloqueada.
        const payload = {
          botId,
          name: form.name.trim(),
          language: form.language,
          category: categoryLocked ? null : form.category,
          ...(hasVars ? { parameterFormat: 'NAMED' } : {}),
          components,
        };
        const res = await apiRequest(getApiUrl(`/whatsapp/templates/${encodeURIComponent(editing.id)}`), {
          method: 'PUT',
          headers: { 'Idempotency-Key': idemKey },
          body: JSON.stringify(payload),
        });
        const data = await leerCuerpo(res);
        if (res.ok && data.success) {
          closeForm();
          if (data.persistedLocally === false) {
            setSubmitSuccess('Plantilla editada en Meta.');
            await reconcile();
          } else {
            setSubmitSuccess(`Plantilla editada. Estado: ${data.status || 'PENDING'}.`);
          }
          loadTemplates();
        } else if (data && data.errorCode === 'TEMPLATE_EDIT_COOLDOWN') {
          closeForm();
          setSubmitError(null);
          setErrorToast(null);
          setCooldownPopup(data.message || 'Meta solo permite editar una plantilla aprobada una vez cada 24 horas.');
        } else {
          failSubmit(describeError(res, data, 'No se pudo editar la plantilla.'));
        }
      } else {
        const payload = {
          botId,
          name: form.name.trim(),
          language: form.language,
          category: form.category,
          ...(hasVars ? { parameterFormat: 'NAMED' } : {}),
          components,
        };
        const res = await apiRequest(getApiUrl('/whatsapp/templates'), {
          method: 'POST',
          headers: { 'Idempotency-Key': idemKey },
          body: JSON.stringify(payload),
        });
        const data = await leerCuerpo(res);
        if (res.ok && data.success) {
          closeForm();
          if (data.persistedLocally === false) {
            setSubmitSuccess(`Plantilla creada en Meta (${data.status || 'PENDING'}). Sincronizando copia local…`);
            await reconcile();
          } else {
            setSubmitSuccess(`Plantilla creada. Estado: ${data.status || 'PENDING'}.`);
          }
          loadTemplates();
        } else {
          failSubmit(describeError(res, data, 'No se pudo crear la plantilla.'));
        }
      }
    } catch (e) {
      failSubmit(`Error de conexión al ${editing ? 'editar' : 'crear'} la plantilla.`);
    } finally {
      setSubmitting(false);
    }
  };

  const pedirEliminar = (name) => {
    if (!name || deletingName) return;
    setPlantillaAEliminar(name);
  };

  const cerrarConfirmacionBorrado = () => {
    if (deletingName) return;
    setPlantillaAEliminar(null);
  };

  const handleDelete = async () => {
    const name = plantillaAEliminar;
    if (!name) return;
    setDeletingName(name);
    try {
      const res = await apiRequest(getApiUrl(`/whatsapp/templates?botId=${botId}&name=${encodeURIComponent(name)}`), {
        method: 'DELETE',
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPlantillaAEliminar(null);
        loadTemplates();
      } else {
        setPlantillaAEliminar(null);
        setError(describeError(res, data, 'No se pudo eliminar la plantilla.'));
      }
    } catch (e) {
      setPlantillaAEliminar(null);
      setError('Error de conexión al eliminar la plantilla.');
    } finally {
      setDeletingName(null);
    }
  };

  // Vista previa: sustituye variables por ejemplos.
  const previewBody = useMemo(() => {
    let text = form.bodyText || '';
    bodyVars.forEach((n) => {
      const ex = (form.bodyExamples[n] || '').trim() || `ejemplo_${n}`;
      text = replaceToken(text, n, ex);
    });
    return text;
  }, [form.bodyText, form.bodyExamples, bodyVars]);

  const previewHeader = useMemo(() => {
    if (form.headerType !== 'TEXT') return '';
    let text = form.headerText || '';
    if (headerHasVar) {
      text = replaceToken(text, headerVars[0], (form.headerTextExample || '').trim() || 'ejemplo');
    }
    return text;
  }, [form.headerType, form.headerText, form.headerTextExample, headerHasVar, headerVars]);

  return (
    <div style={S.container}>
      <SuccessToast message={submitSuccess} onDismiss={() => setSubmitSuccess(null)} />
      <SuccessToast message={errorToast} variant="error" onDismiss={() => setErrorToast(null)} />
      {cooldownPopup ? createPortal(
        <div
          data-testid="cooldown-popup"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cooldown-title"
          style={S.modalOverlay}
        >
          <div style={S.modalCard}>
            <h3 id="cooldown-title" style={S.modalTitle}>No se puede editar ahora</h3>
            <p style={S.modalBody}>{cooldownPopup}</p>
            <div style={S.formActions}>
              <button type="button" style={S.btnPrimary} onClick={() => setCooldownPopup(null)}>
                Entendido
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {plantillaAEliminar ? createPortal(
        <div className="modal-overlay" onClick={cerrarConfirmacionBorrado}>
          <div
            className="modal-content confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-template-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="delete-template-title">Eliminar plantilla</h3>
              <button
                type="button"
                className="modal-close"
                onClick={cerrarConfirmacionBorrado}
                disabled={!!deletingName}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="confirm-modal-body">
              <p>
                ¿Eliminar la plantilla <strong>"{plantillaAEliminar}"</strong>?
              </p>
              <p className="confirm-modal-note">
                Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="admin-btn"
                onClick={cerrarConfirmacionBorrado}
                disabled={!!deletingName}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="admin-btn danger"
                onClick={handleDelete}
                disabled={!!deletingName}
              >
                {deletingName ? 'Eliminando…' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {/* Cabecera */}
      <div style={S.topBar}>
        <div style={S.topBarLeft}>
          <div style={S.waIcon}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff">
              <path d="M17.5 14.4c-.3-.15-1.7-.84-2-.93-.26-.1-.45-.15-.64.15-.19.28-.73.92-.9 1.11-.16.19-.33.21-.61.07-.3-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.73-1.63-2.02-.17-.29-.02-.44.13-.59.13-.13.29-.33.44-.5.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.08-.15-.64-1.55-.88-2.12-.23-.56-.47-.48-.64-.49-.16-.01-.35-.01-.54-.01s-.5.07-.76.36c-.26.29-1 .98-1 2.38s1.02 2.76 1.17 2.95c.14.19 2 3.05 4.85 4.28.68.29 1.2.47 1.61.6.68.22 1.29.18 1.78.11.54-.08 1.7-.69 1.94-1.36.24-.67.24-1.24.17-1.36-.07-.12-.26-.19-.54-.34z"/>
            </svg>
          </div>
          <div>
            <h2 style={S.title}>Plantillas de WhatsApp</h2>
            <p style={S.subtitle}>Crea y gestiona las plantillas de mensaje aprobadas por Meta.</p>
          </div>
        </div>
        {tab === 'manage' && (
          <div style={S.topBarActions}>
            <button style={S.btnGhost} onClick={loadTemplates} disabled={loading}>
              {loading ? 'Cargando…' : 'Actualizar'}
            </button>
            <button
              style={S.btnPrimary}
              onClick={() => { if (showForm) { closeForm(); } else { setEditing(null); setForm(emptyForm()); setShowForm(true); setSubmitError(null); setSubmitSuccess(null); } }}
            >
              {showForm ? 'Cerrar' : '+ Nueva plantilla'}
            </button>
          </div>
        )}
      </div>

      {/* Barra de subpestañas */}
      <div style={S.tabBar}>
        <button
          style={{ ...S.tabItem, ...(tab === 'manage' ? S.tabItemActive : {}) }}
          onClick={() => setTab('manage')}
        >
          Gestión
        </button>
        <button
          style={{ ...S.tabItem, ...(tab === 'send' ? S.tabItemActive : {}) }}
          onClick={() => { setTab('send'); loadTemplates(); }}
        >
          Enviar
        </button>
        <button
          style={{ ...S.tabItem, ...(tab === 'campaigns' ? S.tabItemActive : {}) }}
          onClick={() => { setTab('campaigns'); loadTemplates(); }}
        >
          Campañas
        </button>
      </div>

      {tab === 'send' && (
        <SendTemplateView
          botId={botId}
          templates={templates}
          loading={loading}
          reload={loadTemplates}
          onSuccess={(msg) => { setErrorToast(null); setSubmitSuccess(msg); }}
          onError={(msg) => { setSubmitSuccess(null); setErrorToast(msg); }}
        />
      )}

      {tab === 'campaigns' && (
        <CampanasTab
          botId={botId}
          zonaHoraria={zonaHoraria}
          templates={templates}
          onSuccess={(msg) => { setErrorToast(null); setSubmitSuccess(msg); }}
          onError={(msg) => { setSubmitSuccess(null); setErrorToast(msg); }}
        />
      )}

      {tab === 'manage' && (
      <>
      {error && !showForm && <div style={S.banner('error')}>{error}</div>}

      {/* Formulario de creación */}
      {showForm && (
        <div style={S.formLayout}>
          <div style={S.formCol}>
            <div style={S.formTitleRow}>
              <h3 style={S.formTitle}>{editing ? 'Editar plantilla' : 'Nueva plantilla'}</h3>
              {editing && <span style={{ ...S.statusBadge, background: statusColor(editing.status).bg, color: statusColor(editing.status).fg }}>{editing.status}</span>}
            </div>
            {editing && <div style={S.banner('info')}>Una plantilla aprobada solo se puede editar una vez cada 24 horas. Al guardar vuelve a revisión (PENDING). El nombre y el idioma no se pueden cambiar.</div>}
            {submitError && <div style={S.banner('error')}>{submitError}</div>}

            <div style={S.field}>
              <label style={S.label}>Nombre</label>
              <input
                style={{ ...S.input, ...(editing ? S.inputDisabled : {}) }}
                value={form.name}
                disabled={!!editing}
                placeholder="p.ej. confirmacion_pedido"
                onChange={(e) => update({ name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
              />
              <span style={S.hint}>Solo minúsculas, números y guiones bajos.</span>
            </div>

            <div style={S.row}>
              <div style={{ ...S.field, flex: 1 }}>
                <label style={S.label}>Idioma</label>
                <select style={{ ...S.input, ...(editing ? S.inputDisabled : {}) }} value={form.language} disabled={!!editing} onChange={(e) => update({ language: e.target.value })}>
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label} ({l.code})</option>)}
                </select>
              </div>
              <div style={{ ...S.field, flex: 1 }}>
                <label style={S.label}>Categoría</label>
                <select style={{ ...S.input, ...(categoryLocked ? S.inputDisabled : {}) }} value={form.category} disabled={categoryLocked} onChange={(e) => update({ category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <span style={S.hint}>{categoryLocked ? 'No se puede cambiar la categoría de una plantilla aprobada.' : CATEGORIES.find((c) => c.value === form.category)?.desc}</span>
              </div>
            </div>

            {/* Cabecera */}
            <div style={S.field}>
              <label style={S.label}>Cabecera</label>
              <select style={S.input} value={form.headerType} onChange={(e) => update({ headerType: e.target.value, headerMediaHandle: '', headerMediaFileName: '' })}>
                {HEADER_TYPES.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
            </div>
            {form.headerType === 'TEXT' && (
              <>
                <div style={S.field}>
                  <input
                    style={S.input}
                    value={form.headerText}
                    placeholder="Texto de la cabecera (opcional {{variable}})"
                    maxLength={60}
                    onChange={(e) => update({ headerText: e.target.value })}
                  />
                </div>
                {headerHasVar && (
                  <div style={S.field}>
                    <label htmlFor="tpl-header-example" style={S.label}>Ejemplo para la variable de la cabecera</label>
                    <input id="tpl-header-example" style={S.input} value={form.headerTextExample} onChange={(e) => update({ headerTextExample: e.target.value })} />
                  </div>
                )}
              </>
            )}
            {isMediaHeader && (
              <div style={S.field}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={acceptForHeader}
                  style={{ display: 'none' }}
                  onChange={(e) => handleMediaUpload(e.target.files?.[0])}
                />
                <button style={S.btnUpload} onClick={() => fileInputRef.current?.click()} disabled={mediaUploading}>
                  {mediaUploading ? 'Subiendo…' : form.headerMediaHandle ? '✓ Archivo cargado — cambiar' : `Subir ${form.headerType.toLowerCase()}`}
                </button>
                {form.headerMediaFileName && <span style={S.hint}>{form.headerMediaFileName}</span>}
                {editing && !form.headerMediaHandle && (
                  <span style={{ ...S.hint, color: '#b45309' }}>Vuelve a subir el archivo: al editar hay que reenviar la media de la cabecera.</span>
                )}
              </div>
            )}

            {/* Cuerpo */}
            <div style={S.field}>
              <label style={S.label}>Cuerpo del mensaje *</label>
              <textarea
                style={{ ...S.input, minHeight: 110, resize: 'vertical' }}
                value={form.bodyText}
                placeholder="Hola {{nombre}}, tu pedido {{num_pedido}} está en camino."
                maxLength={1024}
                onChange={(e) => update({ bodyText: e.target.value })}
              />
              <span style={S.hint}>Usa variables con nombre, como {'{{nombre}}'} o {'{{num_pedido}}'}: minúsculas y guiones bajos.</span>
              <div style={S.varRow}>
                <input
                  data-testid="tpl-var-nueva"
                  style={{ ...S.input, flex: 1 }}
                  value={newVarName}
                  placeholder="nombre_variable"
                  onChange={(e) => setNewVarName(e.target.value)}
                />
                <button type="button" style={S.btnSmall} onClick={insertVariable}>Insertar variable</button>
              </div>
            </div>
            {bodyVars.length > 0 && (
              <div style={S.field}>
                <label style={S.label}>Ejemplos de las variables</label>
                {bodyVars.map((n) => (
                  <div key={n} style={S.varRow}>
                    <span style={S.varTag}>{`{{${n}}}`}</span>
                    <input
                      style={{ ...S.input, flex: 1 }}
                      value={form.bodyExamples[n] || ''}
                      placeholder={`Ejemplo para {{${n}}}`}
                      onChange={(e) => update({ bodyExamples: { ...form.bodyExamples, [n]: e.target.value } })}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Pie */}
            <div style={S.field}>
              <label style={S.label}>Pie de página (opcional)</label>
              <input style={S.input} value={form.footerText} maxLength={60} placeholder="p.ej. Responde STOP para baja" onChange={(e) => update({ footerText: e.target.value })} />
            </div>

            {/* Botones */}
            <div style={S.field}>
              <div style={S.rowBetween}>
                <label style={S.label}>Botones (máx. 10)</label>
                <button style={S.btnSmall} onClick={addButton} disabled={form.buttons.length >= 10}>+ Añadir</button>
              </div>
              {form.buttons.map((b, i) => (
                <div key={i} style={S.buttonEditor}>
                  <div style={S.row}>
                    <select style={{ ...S.input, flex: 1 }} value={b.type} onChange={(e) => updateButton(i, { type: e.target.value })}>
                      {BUTTON_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <input style={{ ...S.input, flex: 1 }} value={b.text} placeholder="Texto del botón" maxLength={25} onChange={(e) => updateButton(i, { text: e.target.value })} />
                    <button style={S.btnRemove} onClick={() => removeButton(i)} title="Quitar">×</button>
                  </div>
                  {b.type === 'URL' && (
                    <input style={S.input} value={b.url} placeholder="https://…" onChange={(e) => updateButton(i, { url: e.target.value })} />
                  )}
                  {b.type === 'PHONE_NUMBER' && (
                    <input style={S.input} value={b.phone_number} placeholder="+34600000000" onChange={(e) => updateButton(i, { phone_number: e.target.value })} />
                  )}
                </div>
              ))}
            </div>

            <div style={S.formActions}>
              <button style={S.btnGhost} onClick={closeForm}>Cancelar</button>
              <button style={S.btnPrimary} onClick={handleSubmit} disabled={submitting || mediaUploading}>
                {submitting ? 'Enviando…' : editing ? 'Guardar cambios' : 'Crear plantilla'}
              </button>
            </div>
          </div>

          {/* Vista previa */}
          <div style={S.previewCol}>
            <span style={S.previewTitle}>Vista previa</span>
            <div style={S.phone}>
              <div style={S.bubble}>
                {form.headerType === 'TEXT' && previewHeader && <div style={S.bubbleHeader}>{previewHeader}</div>}
                {isMediaHeader && (
                  <div style={S.bubbleMedia}>
                    {form.headerType === 'IMAGE' ? '🖼️ Imagen' : form.headerType === 'VIDEO' ? '🎬 Vídeo' : '📄 Documento'}
                  </div>
                )}
                <div style={S.bubbleBody}>{previewBody || 'El cuerpo del mensaje aparecerá aquí…'}</div>
                {form.footerText && <div style={S.bubbleFooter}>{form.footerText}</div>}
                <div style={S.bubbleTime}>12:00</div>
              </div>
              {form.buttons.filter((b) => b.text.trim()).length > 0 && (
                <div style={S.bubbleButtons}>
                  {form.buttons.filter((b) => b.text.trim()).map((b, i) => (
                    <div key={i} style={S.previewButton}>
                      {b.type === 'URL' ? '🔗 ' : b.type === 'PHONE_NUMBER' ? '📞 ' : '↩️ '}{b.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Listado */}
      {!showForm && (
        <div style={S.list}>
          {loading && templates.length === 0 && <div style={S.emptyState}>Cargando plantillas…</div>}
          {!loading && templates.length === 0 && !error && (
            <div style={S.emptyState}>
              <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>Aún no hay plantillas</p>
              <p style={{ margin: 0, color: '#6b7280' }}>Crea tu primera plantilla con el botón «Nueva plantilla».</p>
            </div>
          )}
          {templates.map((t) => {
            const sc = statusColor(t.status);
            const numbered = isNumberedTemplate(t);
            const bodyComp = (t.components || []).find((c) => (c.type || '').toUpperCase() === 'BODY');
            const headerComp = (t.components || []).find((c) => (c.type || '').toUpperCase() === 'HEADER');
            const btnComp = (t.components || []).find((c) => (c.type || '').toUpperCase() === 'BUTTONS');
            return (
              <div key={`${t.id || t.name}_${t.language}`} style={S.card}>
                <div style={S.cardHead}>
                  <div style={{ minWidth: 0 }}>
                    <div style={S.cardName}>{t.name}</div>
                    <div style={S.cardMeta}>
                      <span>{t.language}</span>
                      <span style={S.dot}>·</span>
                      <span>{t.category}</span>
                    </div>
                  </div>
                  <span style={{ ...S.statusBadge, background: sc.bg, color: sc.fg }}>{t.status}</span>
                </div>
                {headerComp && (headerComp.text || headerComp.format) && (
                  <div style={S.cardHeader}>{headerComp.text || `[${headerComp.format}]`}</div>
                )}
                {bodyComp?.text && <div style={S.cardBody}>{bodyComp.text}</div>}
                {btnComp?.buttons?.length > 0 && (
                  <div style={S.cardButtons}>
                    {btnComp.buttons.map((b, i) => <span key={i} style={S.cardButtonChip}>{b.text}</span>)}
                  </div>
                )}
                {numbered && (
                  <div data-testid={`tpl-numerada-${t.name}`} style={{ ...S.hint, color: '#b45309' }}>
                    Numerada: recréala con variables con nombre. Meta no deja reutilizar el nombre de una plantilla borrada durante 30 días.
                  </div>
                )}
                <div style={S.cardActions}>
                  {isEditable(t.status) && !numbered && (
                    <button style={S.btnEdit} onClick={() => startEdit(t)}>Editar</button>
                  )}
                  <button
                    style={S.btnDelete}
                    onClick={() => pedirEliminar(t.name)}
                    disabled={deletingName === t.name}
                  >
                    {deletingName === t.name ? 'Eliminando…' : 'Eliminar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
}

const WA_GREEN = '#128C7E';
const WA_GREEN_DARK = '#075E54';

const S = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', background: '#f0f2f5', padding: 20, boxSizing: 'border-box' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 16 },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  waIcon: { width: 42, height: 42, borderRadius: 12, background: WA_GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' },
  subtitle: { margin: '2px 0 0', fontSize: 13, color: '#6b7280' },
  topBarActions: { display: 'flex', gap: 8 },
  btnPrimary: { background: WA_GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(17, 24, 39, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10500, padding: 20,
  },
  modalCard: {
    background: '#fff', borderRadius: 14, padding: '22px 24px', maxWidth: 440, width: '100%',
    boxShadow: '0 18px 45px rgba(0,0,0,0.18)',
  },
  modalTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' },
  modalBody: { margin: '12px 0 0', fontSize: 14, color: '#374151', lineHeight: 1.5 },
  btnGhost: { background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSmall: { background: '#e7f5f2', color: WA_GREEN_DARK, border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSmallInactive: { background: '#f3f4f6', color: '#6b7280' },
  tabBar: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb' },
  tabItem: { background: 'transparent', border: 'none', borderBottom: '2px solid transparent', padding: '10px 16px', fontSize: 14, fontWeight: 600, color: '#6b7280', cursor: 'pointer' },
  tabItemActive: { color: WA_GREEN_DARK, borderBottom: `2px solid ${WA_GREEN}` },
  btnUpload: { background: '#fff', color: WA_GREEN_DARK, border: `1.5px dashed ${WA_GREEN}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%' },
  banner: (kind) => {
    const palette = {
      success: { bg: '#dcfce7', fg: '#166534', bd: '#86efac' },
      error: { bg: '#fee2e2', fg: '#991b1b', bd: '#fca5a5' },
      info: { bg: '#dbeafe', fg: '#1e40af', bd: '#93c5fd' },
    };
    const p = palette[kind] || palette.error;
    return {
      padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12,
      background: p.bg, color: p.fg, border: `1px solid ${p.bd}`,
    };
  },
  formTitleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 },
  formTitle: { margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' },
  inputDisabled: { background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' },
  formLayout: { display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' },
  formCol: { flex: '1 1 420px', minWidth: 300, background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  previewCol: { flex: '0 0 320px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, position: 'sticky', top: 0 },
  field: { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 },
  row: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  rowBetween: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  label: { fontSize: 13, fontWeight: 600, color: '#374151' },
  input: { border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 11px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  hint: { fontSize: 12, color: '#9ca3af' },
  varRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 },
  varTag: { background: '#eef2ff', color: '#4338ca', borderRadius: 6, padding: '4px 8px', fontSize: 12, fontWeight: 700, fontFamily: 'monospace' },
  buttonEditor: { display: 'flex', flexDirection: 'column', gap: 8, padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 8 },
  btnRemove: { background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, width: 36, fontSize: 18, cursor: 'pointer', flexShrink: 0 },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },

  previewTitle: { fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  phone: { width: '100%', background: '#e5ddd5', borderRadius: 14, padding: 14, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)' },
  bubble: { background: '#fff', borderRadius: 8, padding: '8px 10px 6px', boxShadow: '0 1px 1px rgba(0,0,0,0.15)', position: 'relative', maxWidth: '85%' },
  bubbleHeader: { fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 4 },
  bubbleMedia: { background: '#d1d7db', borderRadius: 6, padding: '22px 10px', textAlign: 'center', color: '#54656f', fontSize: 13, marginBottom: 6, fontWeight: 600 },
  bubbleBody: { fontSize: 14, color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.4 },
  bubbleFooter: { fontSize: 12, color: '#8696a0', marginTop: 5 },
  bubbleTime: { fontSize: 10, color: '#8696a0', textAlign: 'right', marginTop: 2 },
  bubbleButtons: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 },
  previewButton: { background: '#fff', color: '#00a5f4', borderRadius: 8, padding: '9px', textAlign: 'center', fontSize: 14, fontWeight: 500, boxShadow: '0 1px 1px rgba(0,0,0,0.1)' },

  list: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 },
  emptyState: { gridColumn: '1 / -1', textAlign: 'center', padding: '48px 20px', color: '#374151', background: '#fff', borderRadius: 12 },
  card: { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: 8 },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 },
  cardName: { fontWeight: 700, fontSize: 15, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardMeta: { display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#6b7280', marginTop: 2, textTransform: 'uppercase' },
  dot: { color: '#d1d5db' },
  statusBadge: { fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0 },
  cardHeader: { fontSize: 13, fontWeight: 600, color: '#374151' },
  cardBody: { fontSize: 13, color: '#4b5563', lineHeight: 1.4, whiteSpace: 'pre-wrap', maxHeight: 84, overflow: 'hidden' },
  cardButtons: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  cardButtonChip: { background: '#f3f4f6', color: '#374151', borderRadius: 6, padding: '3px 8px', fontSize: 12 },
  cardActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4, borderTop: '1px solid #f3f4f6', paddingTop: 10 },
  btnEdit: { background: '#e7f5f2', color: WA_GREEN_DARK, border: '1px solid #b6e3da', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnDelete: { background: 'transparent', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

export default WhatsAppTemplatesPanel;
